import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import {
  Search, Upload, Plus, ChevronRight, ArrowLeft, TrendingUp, TrendingDown,
  Minus, Activity, Loader2, Bell, X, Lightbulb, BarChart3, Rocket, Trash2, AlertTriangle
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stable color per channel — used across summary, trend chart, pills
const CHANNEL_COLORS = {
  Amazon: '#FF9900',
  Flipkart: '#2874F0',
  Google: '#EA4335',
};
const FALLBACK_COLORS = ['#A855F7', '#06B6D4', '#F59E0B', '#EC4899', '#10B981', '#6366F1'];
const colorForChannel = (ch, idx = 0) => CHANNEL_COLORS[ch] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

// Number formatters
const formatVolume = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('en-IN');
};
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
};
// "2026-04" -> "APR '26" (column-header style)
const formatMonthHeader = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${names[Number(mo) - 1]} '${y.slice(2)}`;
};

// Reusable destructive confirmation modal — used for category removal, single
// keyword removal, and bulk keyword removal. Auto-focuses Cancel on mount and
// closes on Escape / scrim click.
function ConfirmDialog({ open, title, description, confirmLabel = 'Remove', onConfirm, onCancel, loading }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,15,15,0.45)' }}
      role="dialog"
      aria-modal="true"
      data-testid="confirm-dialog"
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onCancel?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-rose-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-[#0F0F0F]" data-testid="confirm-title">{title}</h2>
            <div className="text-sm text-gray-600 mt-1.5 leading-relaxed">{description}</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            data-testid="confirm-cancel"
            className="px-3.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-action"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold text-white bg-rose-600 rounded-lg hover:bg-rose-700 disabled:opacity-50"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}



function MoMBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Minus className="w-3 h-3" />—</span>;
  }
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon className="w-3 h-3" />
      {up ? '+' : ''}{pct.toFixed(1)}% MoM
    </span>
  );
}

function CategoryCard({ cat, isActive, onClick, onUpload, onRemove }) {
  // Derive the year dynamically from the current date — never hardcoded so the
  // card automatically reflects "Year: 2027" come Jan 1 without a deploy.
  const currentYear = new Date().getFullYear();
  return (
    <button
      onClick={onClick}
      data-testid={`search-trends-cat-card-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
      className={`group relative text-left bg-white border rounded-xl p-4 transition-all duration-200 hover:shadow-md ${
        isActive ? 'border-[#32B4AC] ring-2 ring-[#32B4AC]/20 shadow-md' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      {/* Remove × — only visible on hover */}
      <span
        role="button"
        tabIndex={0}
        title={`Remove ${cat.category}`}
        aria-label={`Remove ${cat.category}`}
        data-testid={`search-trends-cat-remove-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={(e) => { e.stopPropagation(); onRemove?.(cat); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onRemove?.(cat); } }}
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10 cursor-pointer"
      >
        <X className="w-3 h-3" strokeWidth={2.5} />
      </span>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Category</div>
          <h3 className="text-base font-semibold text-[#0F0F0F]">{cat.category}</h3>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          isActive ? 'bg-[#32B4AC] text-white' : 'bg-[#32B4AC]/10 text-[#32B4AC]'
        }`}>
          <Activity className="w-4 h-4" />
        </div>
      </div>

      {cat.has_data ? (
        <>
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Total search volume</div>
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-2xl font-bold text-[#0F0F0F] tabular-nums">{formatVolume(cat.total_volume)}</div>
            <MoMBadge pct={cat.mom_change_pct} />
          </div>
          <div className="text-[11px] text-gray-500 mt-1">Year: <span className="tabular-nums">{currentYear}</span></div>
          <div className="text-[11px] text-gray-500 mt-0.5 tabular-nums">
            {Number(cat.keyword_count || 0).toLocaleString('en-IN')} keywords
          </div>
        </>
      ) : (
        <div className="py-3">
          <div className="text-xs text-gray-400 mb-2 italic">No data uploaded yet</div>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onUpload(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onUpload(); } }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-[#32B4AC] bg-[#32B4AC]/8 hover:bg-[#32B4AC]/15 rounded-md border border-[#32B4AC]/20 cursor-pointer"
            data-testid={`search-trends-upload-cta-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Upload className="w-3 h-3" />
            Upload data
          </span>
        </div>
      )}
    </button>
  );
}

function AddCategoryDialog({ open, onOpenChange, onAdd }) {
  const [name, setName] = useState('');
  const submit = () => {
    const v = name.trim();
    if (!v) return;
    onAdd(v);
    setName('');
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          placeholder="e.g. Multivitamins"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          data-testid="add-category-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#32B4AC] hover:bg-[#2a9d95] text-white" data-testid="add-category-submit">
            Add Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onOpenChange, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { processed, total } | null

  const pollStatus = async (uploadId) => {
    // Poll the status endpoint every 1.5s until the background task completes.
    while (true) {
      try {
        const r = await fetch(`${API}/upload/search-trends/status/${uploadId}`, {
          credentials: 'include',
        });
        if (!r.ok) {
          throw new Error('Lost contact with upload status (HTTP ' + r.status + ')');
        }
        const doc = await r.json();
        setProgress({
          processed: doc.rows_processed || 0,
          total: doc.total_rows || 0,
          status: doc.status,
        });
        if (doc.status === 'success' || doc.status === 'updated') {
          return doc;
        }
        if (doc.status === 'failed') {
          const err = new Error(doc.error_details || 'Upload failed');
          err.payload = doc;
          throw err;
        }
      } catch (e) {
        throw e;
      }
      await new Promise(res => setTimeout(res, 1500));
    }
  };

  const handleFile = async (file) => {
    setUploading(true);
    setProgress({ processed: 0, total: 0, status: 'uploading' });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/upload/search-trends`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok && res.status !== 202) {
        const text = await res.text().catch(() => '');
        let detail = '';
        try { detail = JSON.parse(text).detail || ''; } catch { detail = text; }
        toast.error(`Upload failed (HTTP ${res.status}): ${detail || res.statusText || 'no response body'}`);
        return;
      }
      const queued = await res.json();
      if (!queued.upload_id) {
        toast.error('Upload could not be scheduled');
        return;
      }
      // Background job accepted — poll for completion.
      const finalDoc = await pollStatus(queued.upload_id);
      toast.success(
        <div>
          <div className="font-semibold">
            Processed {(finalDoc.rows_processed || 0).toLocaleString('en-IN')} rows
          </div>
          <div className="text-xs mt-1 text-gray-600">
            Inserted: {finalDoc.rows_inserted || 0} · Updated: {finalDoc.rows_updated || 0} · Skipped: {finalDoc.rows_skipped || 0}
            {finalDoc.new_categories?.length ? <div>New categories: {finalDoc.new_categories.join(', ')}</div> : null}
            {finalDoc.new_channels?.length ? <div>New channels: {finalDoc.new_channels.join(', ')}</div> : null}
          </div>
        </div>
      );
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('Search Trends upload error:', e);
      const msg = (e?.message && e.message.trim()) || (typeof e === 'string' ? e : '') || 'unknown error (see browser console)';
      toast.error('Upload failed: ' + msg);
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const onDrop = useCallback((files) => {
    if (files.length > 0) handleFile(files[0]);
  }, []); // eslint-disable-line

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    disabled: uploading,
  });

  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) onOpenChange(v); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Search Trends Data</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-600">
          Expected columns:{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">category, channel, month (MM-YYYY), keyword, search_volume</code>
        </div>
        <div
          {...getRootProps()}
          className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            uploading
              ? 'border-gray-200 bg-gray-50 cursor-default'
              : isDragActive
                ? 'border-[#32B4AC] bg-[#32B4AC]/5 cursor-pointer'
                : 'border-gray-300 hover:border-[#32B4AC] hover:bg-gray-50 cursor-pointer'
          }`}
          data-testid="search-trends-dropzone"
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center text-gray-600" data-testid="search-trends-upload-progress">
              <Loader2 className="w-8 h-8 animate-spin mb-2 text-[#32B4AC]" />
              <span className="font-medium">
                {progress?.status === 'uploading' ? 'Uploading file…' : 'Processing in background…'}
              </span>
              {pct !== null && (
                <>
                  <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full mt-3 overflow-hidden">
                    <div
                      className="h-full bg-[#32B4AC] transition-all duration-300"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 mt-1.5 tabular-nums">
                    {progress.processed.toLocaleString('en-IN')} / {progress.total.toLocaleString('en-IN')} rows · {pct}%
                  </span>
                </>
              )}
              <span className="text-[11px] text-gray-400 mt-2">
                Safe to leave this dialog open — large files may take a minute.
              </span>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-700 font-medium mb-1">
                {isDragActive ? 'Drop file here' : 'Drag & drop file here, or click to browse'}
              </p>
              <p className="text-sm text-gray-500">CSV, XLS or XLSX</p>
            </>
          )}
        </div>
        <div className="text-xs text-gray-400 mt-1">
          New categories or channels found in the file are added automatically and become available in filters.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KeyInsightsPanel({ insights }) {
  if (!insights || insights.length === 0) return null;
  const iconFor = (type) => {
    switch (type) {
      case 'channel_leader': return <Activity className="w-4 h-4 text-[#32B4AC]" />;
      case 'channel_growth': return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case 'channel_decline': return <TrendingDown className="w-4 h-4 text-rose-500" />;
      case 'top_keyword': return <Search className="w-4 h-4 text-indigo-500" />;
      case 'breakout_keyword': return <TrendingUp className="w-4 h-4 text-amber-500" />;
      case 'peak_month': return <BarChart3 className="w-4 h-4 text-purple-500" />;
      default: return <Lightbulb className="w-4 h-4 text-gray-400" />;
    }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="search-trends-insights-panel">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gradient-to-r from-amber-50/40 to-transparent">
        <Lightbulb className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-[#0F0F0F]">Key Insights</h3>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {insights.map((it, i) => (
          <div key={i} className="flex items-start gap-2.5 text-sm text-gray-700 leading-snug">
            <div className="mt-0.5 shrink-0">{iconFor(it.type)}</div>
            <p>{it.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelSummaryCards({ summary, activeChannel, onSelect }) {
  if (!summary || summary.length === 0) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="search-trends-channel-summary">
      {summary.map((c, idx) => {
        const color = colorForChannel(c.channel, idx);
        const active = activeChannel === c.channel;
        return (
          <button
            key={c.channel}
            onClick={() => onSelect(active ? 'all' : c.channel)}
            data-testid={`channel-summary-${c.channel.toLowerCase()}`}
            className={`text-left bg-white border rounded-xl p-3.5 transition-all hover:shadow-sm ${
              active ? 'ring-2 ring-offset-1 shadow-md' : 'border-gray-200'
            }`}
            style={active ? { borderColor: color, '--tw-ring-color': color } : {}}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{c.channel}</span>
              </div>
              <MoMBadge pct={c.mom_change_pct} />
            </div>
            <div className="flex items-baseline justify-between">
              <div className="text-lg font-bold text-[#0F0F0F] tabular-nums">{formatVolume(c.latest_volume)}</div>
              <div className="text-[11px] text-gray-400 tabular-nums">{c.share_pct.toFixed(1)}% share</div>
            </div>
            <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.share_pct)}%`, backgroundColor: color }} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function VolumeHeatmap({ heatmap, onViewAll }) {
  const { keywords = [], months = [], matrix = [], max_value = 0, total_unique_keywords = 0 } = heatmap || {};
  if (keywords.length === 0 || months.length === 0) return null;

  // Keywords are already ranked by latest-month volume descending from the backend.
  const shownIndices = keywords.slice(0, 10).map((_, i) => i);
  const totalCount = total_unique_keywords || keywords.length;

  // Color scale: cool (low) → warm (high) — gradient from #f0fdfa to #f59e0b to #dc2626
  const colorFor = (v) => {
    if (!max_value || v === 0) return '#F3F4F6';
    const t = Math.min(1, v / max_value);
    // Piecewise: 0..0.5 teal→amber, 0.5..1 amber→red
    const lerp = (a, b, k) => Math.round(a + (b - a) * k);
    let r, g, b;
    if (t < 0.5) {
      const k = t / 0.5;
      // #CCFBF1 (204,251,241) → #FDE68A (253,230,138)
      r = lerp(204, 253, k); g = lerp(251, 230, k); b = lerp(241, 138, k);
    } else {
      const k = (t - 0.5) / 0.5;
      // #FDE68A (253,230,138) → #DC2626 (220,38,38)
      r = lerp(253, 220, k); g = lerp(230, 38, k); b = lerp(138, 38, k);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };
  const textColor = (v) => (max_value && v / max_value > 0.55 ? '#fff' : '#0F0F0F');

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="search-trends-heatmap">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-500" />
          <h3 className="text-sm font-semibold text-[#0F0F0F]">Search Volume Heatmap</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>Low</span>
          <div className="w-32 h-2 rounded-full" style={{
            background: 'linear-gradient(90deg, #CCFBF1 0%, #FDE68A 50%, #DC2626 100%)'
          }} />
          <span>High</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50/80">
              <th className="text-left py-2 px-4 font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50/80 z-[1]" style={{ minWidth: 180 }}>Keyword</th>
              {months.map(mo => (
                <th key={mo} className="text-center py-2 px-3 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{formatMonth(mo)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shownIndices.map((i) => {
              const kw = keywords[i];
              return (
                <tr key={kw} className="border-t border-gray-100">
                  <td className="py-2 px-4 text-[#0F0F0F] font-medium sticky left-0 bg-white" style={{ minWidth: 180 }}>{kw}</td>
                  {months.map((mo, j) => {
                    const v = matrix[i]?.[j] ?? 0;
                    return (
                      <td key={mo} className="p-1.5 text-center">
                        <div
                          className="rounded px-2 py-1.5 text-center tabular-nums font-medium"
                          style={{ backgroundColor: colorFor(v), color: textColor(v), minWidth: 64 }}
                          title={`${kw} · ${formatMonth(mo)} · ${formatVolume(v)}`}
                        >
                          {formatVolume(v)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalCount > 10 && (
        <div className="px-5 py-3 border-t border-gray-100 text-center bg-gray-50/50">
          <button
            onClick={onViewAll}
            className="text-sm font-medium text-[#32B4AC] hover:text-[#2a9d95] transition-colors"
            data-testid="heatmap-view-all"
          >
            {`View all ${totalCount.toLocaleString('en-IN')} keywords`}
          </button>
        </div>
      )}
    </div>
  );
}

function KeywordsTable({ items, total, latestMonth, previousMonth, onViewAll, onRemoveOne, onBulkRemove }) {
  const prevHeader = previousMonth ? formatMonthHeader(previousMonth) : 'Previous month';
  const latestHeader = latestMonth ? formatMonthHeader(latestMonth) : 'Latest month';
  // Year suffix for the "Total Volume" header — derived from loaded months so it
  // self-updates without backend changes. Single year ('26); cross-year range ('25–'26);
  // empty when no months loaded yet.
  const totalVolumeLabel = useMemo(() => {
    const years = new Set();
    if (latestMonth) years.add(latestMonth.slice(2, 4));
    if (previousMonth) years.add(previousMonth.slice(2, 4));
    if (years.size === 0) return 'Total Volume';
    if (years.size === 1) return `Total Volume '${[...years][0]}`;
    const sorted = [...years].sort();
    return `Total Volume '${sorted[0]}–'${sorted[sorted.length - 1]}`;
  }, [latestMonth, previousMonth]);

  // ── Sorting ──
  // Default: latest month volume descending (matches backend ranking).
  const [sortKey, setSortKey] = useState('latest_volume');
  const [sortDir, setSortDir] = useState('desc');
  const cycleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('desc'); return; }
    setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // ── Search ──
  const [query, setQuery] = useState('');

  // Apply filter, then sort. Numeric sorts use Number() to be safe.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? items.filter(it => (it.keyword || '').toLowerCase().includes(q)) : [...items];
    out.sort((a, b) => {
      const av = Number(a[sortKey] ?? 0);
      const bv = Number(b[sortKey] ?? 0);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return out;
  }, [items, query, sortKey, sortDir]);

  const shown = filtered.slice(0, 10);

  const SortHeader = ({ label, k, align = 'right' }) => {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => cycleSort(k)}
        data-testid={`keywords-sort-${k}`}
        className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'} w-full text-[11px] uppercase tracking-wider font-semibold ${active ? 'text-[#0F0F0F]' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <span>{label}</span>
        <span className="flex flex-col leading-none" aria-hidden>
          <svg className={`w-2.5 h-2.5 -mb-[3px] ${active && sortDir === 'asc' ? 'text-[#32B4AC]' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor"><path d="M0 6L5 0L10 6H0Z"/></svg>
          <svg className={`w-2.5 h-2.5 ${active && sortDir === 'desc' ? 'text-[#32B4AC]' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor"><path d="M0 0L5 6L10 0H0Z"/></svg>
        </span>
      </button>
    );
  };

  // ── Selection ──
  const [selected, setSelected] = useState(() => new Set());
  // Drop selections that no longer exist (e.g. after a successful removal)
  useEffect(() => {
    const present = new Set(shown.map(r => r.keyword));
    setSelected(prev => {
      const next = new Set();
      prev.forEach(k => { if (present.has(k)) next.add(k); });
      return next;
    });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (kw) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(kw)) next.delete(kw); else next.add(kw);
    return next;
  });
  const allShownSelected = shown.length > 0 && shown.every(r => selected.has(r.keyword));
  const someSelected = selected.size > 0;
  const toggleAll = () => setSelected(prev => {
    if (allShownSelected) return new Set();
    const next = new Set(prev);
    shown.forEach(r => next.add(r.keyword));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-[#0F0F0F]">Top Search Terms</h3>
        <div className="flex items-center gap-3 ml-auto">
          <div className="relative w-64">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search keywords..."
              data-testid="keywords-search"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
            />
          </div>
          <span className="text-xs text-gray-400 tabular-nums whitespace-nowrap" data-testid="keywords-unique-count">
            {query ? `${filtered.length.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}` : `${total.toLocaleString('en-IN')}`} unique keywords
          </span>
        </div>
      </div>
      {someSelected && (
        <div
          className="px-5 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
          data-testid="keywords-bulk-bar"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#0F0F0F]">
              <span className="font-semibold tabular-nums">{selected.size}</span> keyword{selected.size === 1 ? '' : 's'} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              data-testid="keywords-bulk-clear"
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={() => onBulkRemove?.(Array.from(selected), clearSelection)}
            data-testid="keywords-bulk-remove"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-rose-600 rounded-lg hover:bg-rose-700"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove selected
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="search-trends-keywords-table">
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '48px' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '44px' }} />
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80">
              <th className="text-center py-2.5 px-2">
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 rounded border-gray-300 accent-[#32B4AC] cursor-pointer"
                  checked={allShownSelected}
                  onChange={toggleAll}
                  aria-label="Select all rows"
                  data-testid="keywords-select-all"
                />
              </th>
              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">#</th>
              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Keyword</th>
              <th className="text-right py-2.5 px-4 whitespace-nowrap"><SortHeader label={totalVolumeLabel} k="total_volume" /></th>
              <th className="text-right py-2.5 px-4 whitespace-nowrap"><SortHeader label={prevHeader} k="previous_volume" /></th>
              <th className="text-right py-2.5 px-4 whitespace-nowrap"><SortHeader label={latestHeader} k="latest_volume" /></th>
              <th className="text-right py-2.5 px-4 whitespace-nowrap"><SortHeader label="M.O.M" k="mom_change_pct" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(!shown || shown.length === 0) ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-400">
                {query ? `No keywords match "${query}"` : 'No keyword data for this view'}
              </td></tr>
            ) : shown.map((it, i) => {
              const isSelected = selected.has(it.keyword);
              return (
                <tr
                  key={it.keyword}
                  className={`group/row border-b border-gray-100 ${isSelected ? 'bg-rose-50/40' : 'hover:bg-gray-50/60'}`}
                  data-testid={`keyword-row-${i}`}
                >
                  <td className="text-center py-2.5 px-2">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 rounded border-gray-300 accent-[#32B4AC] cursor-pointer"
                      checked={isSelected}
                      onChange={() => toggleRow(it.keyword)}
                      aria-label={`Select ${it.keyword}`}
                      data-testid={`keyword-row-check-${i}`}
                    />
                  </td>
                  <td className="py-2.5 px-4 text-gray-400 tabular-nums">{i + 1}</td>
                  <td className="py-2.5 px-4 text-[#0F0F0F] truncate" title={it.keyword}>{it.keyword}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-gray-800 whitespace-nowrap">{formatVolume(it.total_volume)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-gray-600 whitespace-nowrap">{formatVolume(it.previous_volume)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-gray-800 whitespace-nowrap">{formatVolume(it.latest_volume)}</td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap"><MoMBadge pct={it.mom_change_pct} /></td>
                  <td className="py-2.5 px-1 text-right">
                    <button
                      type="button"
                      title={`Remove '${it.keyword}'`}
                      aria-label={`Remove ${it.keyword}`}
                      onClick={() => onRemoveOne?.(it.keyword)}
                      data-testid={`keyword-row-trash-${i}`}
                      className="w-7 h-7 rounded-md inline-flex items-center justify-center text-transparent group-hover/row:text-gray-400 hover:!text-rose-600 hover:!bg-rose-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length > 10 && (
        <div className="px-5 py-3 border-t border-gray-100 text-center bg-gray-50/50">
          <button
            onClick={onViewAll}
            className="text-sm font-medium text-[#32B4AC] hover:text-[#2a9d95] transition-colors"
            data-testid="keywords-table-view-all"
          >
            {`View all ${filtered.length.toLocaleString('en-IN')} keywords`}
          </button>
        </div>
      )}
    </div>
  );
}

function AllKeywordsView({ categoryName, items, latestMonth, previousMonth, onBack, onRemoveOne, onBulkRemove }) {
  // Defensive: sort copy by latest_volume desc (backend already does this, but stay safe if order changes)
  const sorted = useMemo(() => {
    return [...(items || [])].sort((a, b) => (b.latest_volume || 0) - (a.latest_volume || 0));
  }, [items]);
  const prevHeader = previousMonth ? formatMonthHeader(previousMonth) : 'Previous month';
  const latestHeader = latestMonth ? formatMonthHeader(latestMonth) : 'Latest month';
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(it => (it.keyword || '').toLowerCase().includes(q));
  }, [sorted, query]);

  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => {
    const present = new Set(filtered.map(r => r.keyword));
    setSelected(prev => {
      const next = new Set();
      prev.forEach(k => { if (present.has(k)) next.add(k); });
      return next;
    });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (kw) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(kw)) next.delete(kw); else next.add(kw);
    return next;
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.keyword));
  const someSelected = selected.size > 0;
  const toggleAll = () => setSelected(prev => {
    if (allFilteredSelected) return new Set();
    const next = new Set(prev);
    filtered.forEach(r => next.add(r.keyword));
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  return (
    <div className="space-y-4" data-testid="all-keywords-view">
      {/* Local toolbar: back + count + search */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={onBack}
          data-testid="all-keywords-back"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {categoryName || 'category'}
        </button>
        <div className="text-sm text-gray-500">
          <span className="font-semibold text-[#0F0F0F]">{sorted.length.toLocaleString('en-IN')}</span>
          {' '}keywords · sorted by latest month volume
        </div>
        <div className="relative w-72 ml-auto">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keywords..."
            data-testid="all-keywords-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div
          className="px-5 py-2 bg-white border border-gray-200 rounded-xl flex items-center justify-between"
          data-testid="all-keywords-bulk-bar"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#0F0F0F]">
              <span className="font-semibold tabular-nums">{selected.size}</span> keyword{selected.size === 1 ? '' : 's'} selected
            </span>
            <button type="button" onClick={clearSelection} className="text-xs text-gray-500 hover:text-gray-700 underline">Clear</button>
          </div>
          <button
            type="button"
            onClick={() => onBulkRemove?.(Array.from(selected), clearSelection)}
            data-testid="all-keywords-bulk-remove"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-rose-600 rounded-lg hover:bg-rose-700"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="all-keywords-table">
            <colgroup>
              <col style={{ width: '40px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '44px' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80">
                <th className="text-center py-2.5 px-2">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded border-gray-300 accent-[#32B4AC] cursor-pointer"
                    checked={allFilteredSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                    data-testid="all-keywords-select-all"
                  />
                </th>
                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">#</th>
                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Keyword</th>
                <th className="text-right py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">Total Volume</th>
                <th className="text-right py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">{prevHeader}</th>
                <th className="text-right py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">{latestHeader}</th>
                <th className="text-right py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">M.O.M</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-gray-400">
                  {query ? `No keywords match "${query}"` : 'No keyword data available'}
                </td></tr>
              ) : filtered.map((it, i) => {
                const isSelected = selected.has(it.keyword);
                return (
                  <tr
                    key={it.keyword}
                    className={`group/row border-b border-gray-100 ${isSelected ? 'bg-rose-50/40' : 'hover:bg-gray-50/60'}`}
                  >
                    <td className="text-center py-2.5 px-2">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 rounded border-gray-300 accent-[#32B4AC] cursor-pointer"
                        checked={isSelected}
                        onChange={() => toggleRow(it.keyword)}
                        aria-label={`Select ${it.keyword}`}
                        data-testid={`all-keyword-row-check-${i}`}
                      />
                    </td>
                    <td className="py-2.5 px-4 text-gray-400 tabular-nums">{i + 1}</td>
                    <td className="py-2.5 px-4 text-[#0F0F0F] truncate" title={it.keyword}>{it.keyword}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-gray-800 whitespace-nowrap">{formatVolume(it.total_volume)}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-gray-600 whitespace-nowrap">{formatVolume(it.previous_volume)}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-gray-800 whitespace-nowrap">{formatVolume(it.latest_volume)}</td>
                    <td className="py-2.5 px-4 text-right whitespace-nowrap"><MoMBadge pct={it.mom_change_pct} /></td>
                    <td className="py-2.5 px-1 text-right">
                      <button
                        type="button"
                        title={`Remove '${it.keyword}'`}
                        aria-label={`Remove ${it.keyword}`}
                        onClick={() => onRemoveOne?.(it.keyword)}
                        data-testid={`all-keyword-row-trash-${i}`}
                        className="w-7 h-7 rounded-md inline-flex items-center justify-center text-transparent group-hover/row:text-gray-400 hover:!text-rose-600 hover:!bg-rose-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AllKeywordsHeatmapView({ categoryName, heatmap, onBack }) {
  const { keywords = [], months = [], matrix = [], max_value = 0 } = heatmap || {};
  const [query, setQuery] = useState('');

  // Backend already orders keywords by latest-month volume descending.
  // Build an index list so we can filter on the keyword name without dropping
  // the row's matrix lookup.
  const indexed = useMemo(() => keywords.map((kw, i) => ({ kw, i })), [keywords]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return indexed;
    return indexed.filter(x => (x.kw || '').toLowerCase().includes(q));
  }, [indexed, query]);

  // Same color logic as VolumeHeatmap (kept in sync intentionally — visual parity).
  const colorFor = (v) => {
    if (!max_value || v === 0) return '#F3F4F6';
    const t = Math.min(1, v / max_value);
    const lerp = (a, b, k) => Math.round(a + (b - a) * k);
    let r, g, b;
    if (t < 0.5) {
      const k = t / 0.5;
      r = lerp(204, 253, k); g = lerp(251, 230, k); b = lerp(241, 138, k);
    } else {
      const k = (t - 0.5) / 0.5;
      r = lerp(253, 220, k); g = lerp(230, 38, k); b = lerp(138, 38, k);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };
  const textColor = (v) => (max_value && v / max_value > 0.55 ? '#fff' : '#0F0F0F');

  return (
    <div className="space-y-4" data-testid="all-heatmap-view">
      {/* Local toolbar: back + count + search + legend */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={onBack}
          data-testid="all-heatmap-back"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {categoryName || 'category'}
        </button>
        <div className="text-sm text-gray-500">
          <span className="font-semibold text-[#0F0F0F]">{indexed.length.toLocaleString('en-IN')}</span>
          {' '}keywords · sorted by latest month volume
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 ml-auto">
          <span>Low</span>
          <div className="w-32 h-2 rounded-full" style={{
            background: 'linear-gradient(90deg, #CCFBF1 0%, #FDE68A 50%, #DC2626 100%)'
          }} />
          <span>High</span>
        </div>
        <div className="relative w-72">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keywords..."
            data-testid="all-heatmap-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
          />
        </div>
      </div>

      {/* Heatmap table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="all-heatmap-table">
            <thead>
              <tr className="bg-gray-50/80">
                <th
                  className="text-left py-2 px-4 font-semibold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50/80 z-[1]"
                  style={{ minWidth: 220 }}
                >
                  Keyword
                </th>
                {months.map(mo => (
                  <th key={mo} className="text-center py-2 px-3 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {formatMonth(mo)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={months.length + 1} className="text-center py-12 text-gray-400">
                    {query ? `No keywords match "${query}"` : 'No keyword data available'}
                  </td>
                </tr>
              ) : filtered.map(({ kw, i }) => (
                <tr key={kw} className="border-t border-gray-100">
                  <td className="py-2 px-4 text-[#0F0F0F] font-medium sticky left-0 bg-white truncate" style={{ minWidth: 220, maxWidth: 360 }} title={kw}>
                    {kw}
                  </td>
                  {months.map((mo, j) => {
                    const v = matrix[i]?.[j] ?? 0;
                    return (
                      <td key={mo} className="p-1.5 text-center">
                        <div
                          className="rounded px-2 py-1.5 text-center tabular-nums font-medium"
                          style={{ backgroundColor: colorFor(v), color: textColor(v), minWidth: 64 }}
                          title={`${kw} · ${formatMonth(mo)} · ${formatVolume(v)}`}
                        >
                          {formatVolume(v)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Movers & Shakers ────────────────────────────────────────────────────────
const escapeRegExp = (s) => (s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function HighlightedKeyword({ text, query }) {
  if (!query) return <>{text}</>;
  const re = new RegExp(`(${escapeRegExp(query)})`, 'ig');
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) =>
        re.test(p)
          ? <mark key={i} className="bg-amber-100 text-amber-900 rounded px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function TrendBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Minus className="w-3 h-3" />—</span>;
  }
  const up = pct >= 0;
  const cls = up ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700';
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[11px] font-semibold tabular-nums ${cls}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {up ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
}

/**
 * Single source of truth for the Movers & Shakers UI. Used in two surfaces:
 *  1. The compact in-page section on the Search Trends detail view (top-10).
 *  2. The full-page "All movers" view (no row limit), reached via the
 *     "View all N movers" link — see `MoversAndShakersSection` parent.
 *
 * Renders header (rocket + title + time-window toggle + channel pills),
 * filter bar (min prev vol, min growth %, sort toggle), search input with
 * result count, and the data table itself.
 */
function MoversTable({
  category,
  channelOptions,
  showAllRows = false,
  onViewAll,
  onBack,
}) {
  const [data, setData] = useState({ movers: [], latest_month: null, previous_month: null });
  const [loading, setLoading] = useState(false);
  const [window, setWindow] = useState(1); // 1 = 1mo, 3 = 3mo
  const [channel, setChannel] = useState('all');
  const [minPrevVol, setMinPrevVol] = useState(10000);
  const [minGrowth, setMinGrowth] = useState(5);
  const [sortBy, setSortBy] = useState('growth'); // 'growth' | 'score'
  // Column-header sort (overrides `sortBy` when set). Cycles desc → asc → off on
  // repeat clicks of the same column, matching the Top Search Terms behaviour.
  const [colSortKey, setColSortKey] = useState(null); // 'previous_volume' | 'latest_volume' | 'mom_change_pct' | null
  const [colSortDir, setColSortDir] = useState('desc');
  const cycleColSort = (k) => {
    if (colSortKey !== k) { setColSortKey(k); setColSortDir('desc'); return; }
    if (colSortDir === 'desc') { setColSortDir('asc'); return; }
    setColSortKey(null); setColSortDir('desc');  // 3rd click → clear
  };
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    const params = new URLSearchParams({ category, window: String(window) });
    if (channel && channel !== 'all') params.set('channel', channel);
    fetch(`${API}/search-trends/movers?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { movers: [], latest_month: null, previous_month: null })
      .then(setData)
      .catch(() => setData({ movers: [], latest_month: null, previous_month: null }))
      .finally(() => setLoading(false));
  }, [category, channel, window]);

  // Apply user filters + search + sort. Backend already excluded 0-prev-vol rows.
  const filtered = useMemo(() => {
    const movers = data.movers || [];
    const q = (query || '').trim().toLowerCase();
    let out = movers.filter(m =>
      (m.previous_volume || 0) >= (Number(minPrevVol) || 0) &&
      (m.mom_change_pct || 0) >= (Number(minGrowth) || 0)
    );
    if (q) out = out.filter(m => (m.keyword || '').toLowerCase().includes(q));
    // Column-header sort wins over the global Sort toggle.
    if (colSortKey) {
      out = [...out].sort((a, b) => {
        const av = Number(a[colSortKey] ?? 0);
        const bv = Number(b[colSortKey] ?? 0);
        return colSortDir === 'asc' ? av - bv : bv - av;
      });
    } else if (sortBy === 'score') {
      out = [...out].sort((a, b) => (b.score || 0) - (a.score || 0));
    } else {
      out = [...out].sort((a, b) => (b.mom_change_pct || 0) - (a.mom_change_pct || 0));
    }
    return out;
  }, [data, minPrevVol, minGrowth, sortBy, query, colSortKey, colSortDir]);

  const rows = showAllRows ? filtered : filtered.slice(0, 10);
  const prevHeader = data.previous_month ? formatMonthHeader(data.previous_month) : 'Prev month';
  const latestHeader = data.latest_month ? formatMonthHeader(data.latest_month) : 'Latest month';
  // Year suffix for the "Total Volume" header — same logic as KeywordsTable so the
  // two surfaces stay in sync. Single year → "Total Volume '26"; cross-year → range.
  const totalVolumeLabel = useMemo(() => {
    const years = new Set();
    if (data.latest_month) years.add(data.latest_month.slice(2, 4));
    if (data.previous_month) years.add(data.previous_month.slice(2, 4));
    if (years.size === 0) return 'Total Volume';
    if (years.size === 1) return `Total Volume '${[...years][0]}`;
    const sorted = [...years].sort();
    return `Total Volume '${sorted[0]}–'${sorted[sorted.length - 1]}`;
  }, [data.latest_month, data.previous_month]);
  const baselineCount = (data.movers || []).filter(m =>
    (m.previous_volume || 0) >= (Number(minPrevVol) || 0) &&
    (m.mom_change_pct || 0) >= (Number(minGrowth) || 0)
  ).length;

  const resetFilters = () => {
    setMinPrevVol(10000);
    setMinGrowth(5);
    setQuery('');
    setSortBy('growth');
  };

  return (
    <div className="bg-white border border-gray-200/80 rounded-xl shadow-sm overflow-hidden" data-testid="movers-and-shakers">
      {/* Header */}
      <header className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {onBack ? (
            <button
              onClick={onBack}
              data-testid="movers-back"
              className="inline-flex items-center gap-2 px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white transition-colors mr-2"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : null}
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
            <Rocket className="w-4 h-4 text-white" strokeWidth={2.25} />
          </div>
          <h3 className="text-sm font-semibold text-[#0F0F0F]">
            {showAllRows ? 'All Movers & Shakers' : 'Movers & Shakers'}
          </h3>
          <span className="text-[11px] text-gray-400 ml-1">keywords accelerating month-over-month</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Time-window toggle */}
          <div className="flex items-center bg-gray-100 rounded-md p-0.5" data-testid="movers-window-toggle">
            {[1, 3].map(w => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                data-testid={`movers-window-${w}mo`}
                className={`px-2.5 py-1 text-[11px] font-semibold uppercase rounded transition-all ${window === w ? 'bg-white text-[#0F0F0F] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {w} mo
              </button>
            ))}
          </div>
          {/* Channel pills */}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="movers-channel-pills">
            {[{ id: 'all', label: 'All' }, ...(channelOptions || []).map(c => ({ id: c, label: c }))].map((p, idx) => {
              const active = channel === p.id;
              const color = p.id === 'all' ? '#32B4AC' : colorForChannel(p.id, idx);
              return (
                <button
                  key={p.id}
                  onClick={() => setChannel(p.id)}
                  data-testid={`movers-channel-${p.id.toLowerCase()}`}
                  className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-all border ${
                    active ? 'text-white shadow-sm' : 'text-gray-600 bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  style={active ? { backgroundColor: color, borderColor: color } : {}}
                >
                  {p.id !== 'all' && (
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: active ? '#fff' : color }} />
                  )}
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Filter bar */}
      <div className="px-5 py-3 bg-gray-50/60 border-b border-gray-100 flex items-center gap-5 flex-wrap">
        <label className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Min. prev month volume</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-[10.5px] font-semibold tabular-nums">≥ {formatVolume(minPrevVol)}</span>
          <input
            type="number"
            value={minPrevVol}
            onChange={(e) => setMinPrevVol(Number(e.target.value) || 0)}
            data-testid="movers-min-prev-vol"
            className="w-24 h-7 text-sm px-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20 tabular-nums"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Min. growth %</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-[10.5px] font-semibold tabular-nums">≥ {minGrowth}%</span>
          <input
            type="number"
            value={minGrowth}
            onChange={(e) => setMinGrowth(Number(e.target.value) || 0)}
            data-testid="movers-min-growth"
            className="w-20 h-7 text-sm px-2 border border-gray-300 rounded-md focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20 tabular-nums"
          />
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Sort</span>
          <div className="flex items-center bg-white border border-gray-200 rounded-md p-0.5" data-testid="movers-sort-toggle">
            {[{ id: 'growth', label: '% Growth' }, { id: 'score', label: 'Score' }].map(o => (
              <button
                key={o.id}
                onClick={() => setSortBy(o.id)}
                data-testid={`movers-sort-${o.id}`}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-all ${sortBy === o.id ? 'bg-[#0F0F0F] text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search + count */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keywords..."
            data-testid="movers-search"
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#32B4AC] focus:ring-1 focus:ring-[#32B4AC]/20"
          />
        </div>
        <span className="text-xs text-gray-500 ml-auto tabular-nums" data-testid="movers-count">
          Showing <span className="font-semibold text-[#0F0F0F]">{filtered.length}</span> of <span className="font-semibold text-[#0F0F0F]">{baselineCount}</span> keywords
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="movers-table">
          <colgroup>
            <col style={{ width: '60px' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '160px' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200">
              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-[11px] uppercase tracking-wider">#</th>
              <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-[11px] uppercase tracking-wider">Keyword</th>
              {(() => {
                // Inline SortHeader for Movers — keeps the same triangle-arrow visual
                // used by Top Search Terms. Right-aligned column.
                const Hdr = ({ k, label }) => {
                  const active = colSortKey === k;
                  return (
                    <button
                      type="button"
                      onClick={() => cycleColSort(k)}
                      data-testid={`movers-sort-${k}`}
                      className={`inline-flex items-center gap-1 justify-end w-full text-[11px] uppercase tracking-wider font-semibold ${active ? 'text-[#0F0F0F]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span>{label}</span>
                      <span className="flex flex-col leading-none" aria-hidden>
                        <svg className={`w-2.5 h-2.5 -mb-[3px] ${active && colSortDir === 'asc' ? 'text-[#32B4AC]' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor"><path d="M0 6L5 0L10 6H0Z"/></svg>
                        <svg className={`w-2.5 h-2.5 ${active && colSortDir === 'desc' ? 'text-[#32B4AC]' : 'text-gray-300'}`} viewBox="0 0 10 6" fill="currentColor"><path d="M0 0L5 6L10 0H0Z"/></svg>
                      </span>
                    </button>
                  );
                };
                return (
                  <>
                    <th className="text-right py-2.5 px-4 whitespace-nowrap"><Hdr k="total_volume" label={totalVolumeLabel} /></th>
                    <th className="text-right py-2.5 px-4 whitespace-nowrap"><Hdr k="previous_volume" label={prevHeader} /></th>
                    <th className="text-right py-2.5 px-4 whitespace-nowrap"><Hdr k="latest_volume" label={latestHeader} /></th>
                    <th className="text-right py-2.5 px-4 whitespace-nowrap"><Hdr k="mom_change_pct" label="Trend" /></th>
                  </>
                );
              })()}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="py-8 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline-block text-[#32B4AC]" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="6" className="py-10 text-center text-gray-400">
                No movers match these filters.
                <button onClick={resetFilters} data-testid="movers-reset" className="ml-1 text-[#32B4AC] hover:underline">
                  Reset filters
                </button>
              </td></tr>
            ) : rows.map((m, i) => (
              <tr key={m.keyword} className="border-b border-gray-100 hover:bg-gray-50/60">
                <td className="py-2.5 px-4 text-gray-400 tabular-nums">{i + 1}</td>
                <td className="py-2.5 px-4 text-[#0F0F0F] truncate" title={m.keyword}>
                  <HighlightedKeyword text={m.keyword} query={query} />
                </td>
                <td className="py-2.5 px-4 text-right text-gray-800 tabular-nums whitespace-nowrap">{formatVolume(m.total_volume)}</td>
                <td className="py-2.5 px-4 text-right text-gray-600 tabular-nums whitespace-nowrap">{formatVolume(m.previous_volume)}</td>
                <td className="py-2.5 px-4 text-right text-gray-900 tabular-nums whitespace-nowrap">{formatVolume(m.latest_volume)}</td>
                <td className="py-2.5 px-4 text-right whitespace-nowrap"><TrendBadge pct={m.mom_change_pct} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View-all footer (only on the compact view) */}
      {!showAllRows && filtered.length > 10 && (
        <div className="px-5 py-3 border-t border-gray-100 text-center bg-gray-50/50">
          <button
            onClick={onViewAll}
            data-testid="movers-view-all"
            className="text-sm font-medium text-[#32B4AC] hover:text-[#2a9d95] transition-colors"
          >
            {`View all ${filtered.length.toLocaleString('en-IN')} movers`}
          </button>
        </div>
      )}
    </div>
  );
}


export default function SearchTrends({ user }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], channels: [] });
  const [summary, setSummary] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = overview
  const [activeChannel, setActiveChannel] = useState('all');
  const [trend, setTrend] = useState({ months: [], series: {}, category: '' });
  const [keywords, setKeywords] = useState({ keywords: [], total_unique_keywords: 0, latest_month: null, previous_month: null });
  const [insights, setInsights] = useState({ channel_summary: [], insights: [], heatmap: { keywords: [], months: [], matrix: [], max_value: 0 } });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [allKeywordsOpen, setAllKeywordsOpen] = useState(false);
  const [allHeatmapOpen, setAllHeatmapOpen] = useState(false);
  const [allMoversOpen, setAllMoversOpen] = useState(false);
  // Removal flows: { kind: 'category'|'keyword-one'|'keyword-bulk', payload: ... } or null
  const [removalState, setRemovalState] = useState(null);
  const [removalLoading, setRemovalLoading] = useState(false);

  // Re-fetch the detail panel (trend + keywords + insights) without touching
  // selectedCategory / activeChannel. Used after a successful keyword removal
  // so the Top Search Terms table, Heatmap and Movers & Shakers section all
  // refresh together — and the "unique keywords" count drops.
  const refreshDetail = useCallback(async () => {
    if (!selectedCategory) return;
    const params = new URLSearchParams({ category: selectedCategory });
    if (activeChannel && activeChannel !== 'all') params.set('channel', activeChannel);
    try {
      const [t, k, ins] = await Promise.all([
        fetch(`${API}/search-trends/trend?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { months: [], series: {} }),
        fetch(`${API}/search-trends/keywords?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { keywords: [], total_unique_keywords: 0, latest_month: null, previous_month: null }),
        fetch(`${API}/search-trends/insights?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { channel_summary: [], insights: [], heatmap: { keywords: [], months: [], matrix: [], max_value: 0 } }),
      ]);
      setTrend(t); setKeywords(k); setInsights(ins);
    } catch (e) { /* swallow — UI just keeps previous data */ }
  }, [selectedCategory, activeChannel]);

  const confirmRemoval = async () => {
    if (!removalState) return;
    setRemovalLoading(true);
    try {
      if (removalState.kind === 'category') {
        const name = removalState.payload;
        const res = await fetch(`${API}/search-trends/categories/${encodeURIComponent(name)}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!res.ok) {
          toast.error('Could not remove category');
        } else {
          toast.success(`Removed "${name}"`);
          if (selectedCategory === name) setSelectedCategory(null);
          await fetchMetaAndSummary();
        }
      } else if (removalState.kind === 'keyword-one' || removalState.kind === 'keyword-bulk') {
        const { keywords: kws, clear } = removalState.payload;
        const res = await fetch(`${API}/search-trends/keywords/remove`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category: selectedCategory, keywords: kws }),
        });
        if (!res.ok) {
          toast.error('Could not remove keyword(s)');
        } else {
          const data = await res.json().catch(() => ({}));
          toast.success(kws.length === 1
            ? `Removed "${kws[0]}"`
            : `Removed ${kws.length} keywords`);
          clear?.();
          await refreshDetail();
          await fetchMetaAndSummary(); // category cards may show a different top keyword now
        }
      }
    } catch (e) {
      toast.error('Unexpected error');
    } finally {
      setRemovalLoading(false);
      setRemovalState(null);
    }
  };

  const fetchMetaAndSummary = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        fetch(`${API}/search-trends/meta`, { credentials: 'include' }),
        fetch(`${API}/search-trends/summary`, { credentials: 'include' }),
      ]);
      if (mRes.ok) {
        const m = await mRes.json();
        // Defensive: filter out deprecated channels (e.g. OSC) in case prod meta
        // hasn't been re-seeded yet. Backend startup hook handles it too.
        const filteredChannels = (m.channels || []).filter(c => c !== 'OSC');
        setMeta({ ...m, channels: filteredChannels });
      }
      if (sRes.ok) {
        const data = await sRes.json();
        setSummary(data.categories || []);
        if (data.channels?.length) setMeta(prev => ({ ...prev, channels: data.channels }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMetaAndSummary(); }, [fetchMetaAndSummary]);

  // Fetch detail (trend + keywords + insights) when a category is selected
  useEffect(() => {
    if (!selectedCategory) return;
    // Reset the all-keywords / all-heatmap views whenever category or channel changes
    setAllKeywordsOpen(false);
    setAllHeatmapOpen(false);
    setAllMoversOpen(false);
    const params = new URLSearchParams({ category: selectedCategory });
    if (activeChannel && activeChannel !== 'all') params.set('channel', activeChannel);
    Promise.all([
      fetch(`${API}/search-trends/trend?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { months: [], series: {} }),
      fetch(`${API}/search-trends/keywords?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { keywords: [], total_unique_keywords: 0, latest_month: null, previous_month: null }),
      fetch(`${API}/search-trends/insights?${params}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { channel_summary: [], insights: [], heatmap: { keywords: [], months: [], matrix: [], max_value: 0 } }),
    ]).then(([t, k, ins]) => { setTrend(t); setKeywords(k); setInsights(ins); }).catch(console.error);
  }, [selectedCategory, activeChannel]);

  const handleAddCategory = async (name) => {
    try {
      const res = await fetch(`${API}/search-trends/categories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add category');
        return;
      }
      const data = await res.json();
      setMeta(prev => ({ ...prev, categories: data.categories }));
      toast.success(`Added category "${name}"`);
      setAddCatOpen(false);
      fetchMetaAndSummary();
    } catch (e) {
      toast.error('Failed to add category');
    }
  };

  // Trend chart data: rows of { month, label, [channel]: volume }
  const chartData = useMemo(() => {
    if (!trend.months?.length) return [];
    const seriesChannels = activeChannel === 'all'
      ? Object.keys(trend.series || {})
      : [activeChannel].filter(ch => trend.series?.[ch]);
    return trend.months.map((mo, i) => {
      const row = { month: mo, label: formatMonth(mo) };
      seriesChannels.forEach(ch => { row[ch] = trend.series[ch]?.[i] ?? 0; });
      // total across visible channels
      row.__total = seriesChannels.reduce((s, ch) => s + (trend.series[ch]?.[i] ?? 0), 0);
      return row;
    });
  }, [trend, activeChannel]);

  const lineChannels = useMemo(() => {
    if (activeChannel === 'all') return Object.keys(trend.series || {});
    return [activeChannel];
  }, [trend, activeChannel]);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="search-trends" />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#32B4AC]"
                  data-testid="search-trends-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              <h1 className="text-2xl font-bold text-[#0F0F0F] flex items-center gap-2">
                <Search className="w-6 h-6 text-[#32B4AC]" />
                Search Trends
                {selectedCategory && (
                  <>
                    <ChevronRight className="w-5 h-5 text-gray-300" />
                    <span className="text-[#32B4AC]">{selectedCategory}</span>
                  </>
                )}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setAddCatOpen(true)}
                data-testid="search-trends-add-cat-btn"
                className="h-9"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add Category
              </Button>
              <Button
                onClick={() => setUploadOpen(true)}
                className="bg-[#32B4AC] hover:bg-[#2a9d95] text-white h-9"
                data-testid="search-trends-upload-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" /> Upload Data
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Category cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="search-trends-cat-grid">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-40" />
                ))
              ) : (
                meta.categories.map(catName => {
                  const cat = summary.find(c => c.category === catName) || {
                    category: catName, total_volume: 0, keyword_count: 0,
                    sparkline: [], mom_change_pct: null, top_keyword: null, has_data: false,
                  };
                  return (
                    <CategoryCard
                      key={catName}
                      cat={cat}
                      isActive={selectedCategory === catName}
                      onClick={() => { setSelectedCategory(catName); setActiveChannel('all'); }}
                      onUpload={() => setUploadOpen(true)}
                      onRemove={(c) => setRemovalState({ kind: 'category', payload: c.category })}
                    />
                  );
                })
              )}
            </div>

            {/* Detail view OR overview hint */}
            {selectedCategory && allKeywordsOpen ? (
              <AllKeywordsView
                categoryName={selectedCategory}
                items={keywords.keywords}
                latestMonth={keywords.latest_month}
                previousMonth={keywords.previous_month}
                onBack={() => setAllKeywordsOpen(false)}
                onRemoveOne={(kw) => setRemovalState({ kind: 'keyword-one', payload: { keywords: [kw] } })}
                onBulkRemove={(kws, clear) => setRemovalState({ kind: 'keyword-bulk', payload: { keywords: kws, clear } })}
              />
            ) : selectedCategory && allHeatmapOpen ? (
              <div>
                <h2 className="text-lg font-semibold text-[#0F0F0F] mb-3" data-testid="all-heatmap-title">
                  All Keywords Heatmap{selectedCategory ? ` — ${selectedCategory}` : ''}
                </h2>
                <AllKeywordsHeatmapView
                  categoryName={selectedCategory}
                  heatmap={insights.heatmap}
                  onBack={() => setAllHeatmapOpen(false)}
                />
              </div>
            ) : selectedCategory && allMoversOpen ? (
              <MoversTable
                category={selectedCategory}
                channelOptions={meta.channels}
                showAllRows
                onBack={() => setAllMoversOpen(false)}
              />
            ) : selectedCategory ? (
              <>
                {/* Channel filter pills */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="search-trends-channel-pills">
                  {[{ id: 'all', label: 'All Channels' }, ...meta.channels.map(c => ({ id: c, label: c }))].map((p, idx) => {
                    const active = activeChannel === p.id;
                    const color = p.id === 'all' ? '#32B4AC' : colorForChannel(p.id, idx);
                    return (
                      <button
                        key={p.id}
                        onClick={() => setActiveChannel(p.id)}
                        data-testid={`channel-pill-${p.id.toLowerCase()}`}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all border ${
                          active
                            ? 'text-white shadow-md'
                            : 'text-gray-600 bg-white border-gray-200 hover:border-gray-300'
                        }`}
                        style={active ? { backgroundColor: color, borderColor: color } : {}}
                      >
                        {p.id !== 'all' && (
                          <span
                            className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                            style={{ backgroundColor: active ? '#fff' : color }}
                          />
                        )}
                        {p.label}
                      </button>
                    );
                  })}
                </div>

                {/* Trend chart */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">
                        Total Search Volume Over Time
                        <span className="text-gray-400 font-normal ml-1">
                          ({activeChannel === 'all' ? 'All Channels' : activeChannel})
                        </span>
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">{trend.months?.length || 0} months tracked</p>
                    </div>
                  </div>
                  {chartData.length === 0 ? (
                    <div className="h-72 flex flex-col items-center justify-center text-gray-400">
                      <Activity className="w-10 h-10 mb-2 text-gray-300" />
                      <p className="text-sm">No data for this selection</p>
                      <button
                        onClick={() => setUploadOpen(true)}
                        className="mt-3 text-sm text-[#32B4AC] hover:underline"
                        data-testid="search-trends-empty-upload-btn"
                      >
                        Upload data →
                      </button>
                    </div>
                  ) : (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                          <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} tickFormatter={formatVolume} />
                          <Tooltip
                            formatter={(v, name) => [formatVolume(v), name]}
                            labelStyle={{ color: '#0F0F0F', fontWeight: 600 }}
                            contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 8 }}
                          />
                          <Legend iconType="line" wrapperStyle={{ fontSize: 12 }} />
                          {lineChannels.map((ch, idx) => (
                            <Line
                              key={ch}
                              type="monotone"
                              dataKey={ch}
                              stroke={colorForChannel(ch, idx)}
                              strokeWidth={2.25}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                              isAnimationActive={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Channel summary cards (click to filter) */}
                <ChannelSummaryCards
                  summary={insights.channel_summary}
                  activeChannel={activeChannel}
                  onSelect={setActiveChannel}
                />

                {/* Key Insights — narrative bullets */}
                <KeyInsightsPanel insights={insights.insights} />

                {/* Keywords table */}
                <KeywordsTable
                  items={keywords.keywords}
                  total={keywords.total_unique_keywords}
                  latestMonth={keywords.latest_month}
                  previousMonth={keywords.previous_month}
                  onViewAll={() => setAllKeywordsOpen(true)}
                  onRemoveOne={(kw) => setRemovalState({ kind: 'keyword-one', payload: { keywords: [kw] } })}
                  onBulkRemove={(kws, clear) => setRemovalState({ kind: 'keyword-bulk', payload: { keywords: kws, clear } })}
                />

                {/* Movers & Shakers — accelerating keywords (sits between Top Search Terms and Heatmap) */}
                <MoversTable
                  category={selectedCategory}
                  channelOptions={meta.channels}
                  onViewAll={() => setAllMoversOpen(true)}
                />

                {/* Heatmap: keywords × months with intensity */}
                <VolumeHeatmap heatmap={insights.heatmap} onViewAll={() => setAllHeatmapOpen(true)} />
              </>
            ) : (
              <div className="bg-gradient-to-br from-[#32B4AC]/5 to-transparent border border-dashed border-[#32B4AC]/30 rounded-xl p-6 text-center">
                <Search className="w-8 h-8 mx-auto text-[#32B4AC] mb-2" />
                <p className="text-sm text-gray-600">
                  Select a category card above to see its monthly search-volume trend across channels and top keywords.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={fetchMetaAndSummary}
      />
      <AddCategoryDialog
        open={addCatOpen}
        onOpenChange={setAddCatOpen}
        onAdd={handleAddCategory}
      />

      {/* Destructive confirmation dialog — drives category + keyword removal */}
      <ConfirmDialog
        open={!!removalState}
        loading={removalLoading}
        onCancel={() => { if (!removalLoading) setRemovalState(null); }}
        onConfirm={confirmRemoval}
        confirmLabel={
          removalState?.kind === 'keyword-bulk'
            ? `Remove ${removalState.payload.keywords.length} keywords`
            : 'Remove'
        }
        title={
          removalState?.kind === 'category'
            ? `Remove ${removalState.payload}?`
            : removalState?.kind === 'keyword-one'
              ? `Remove '${removalState.payload.keywords[0]}'?`
              : removalState?.kind === 'keyword-bulk'
                ? `Remove ${removalState.payload.keywords.length} keywords?`
                : ''
        }
        description={
          removalState?.kind === 'category'
            ? 'This will permanently delete this category and all its associated data. This cannot be undone.'
            : removalState?.kind === 'keyword-one'
              ? <>This keyword will be removed from the <span className="font-medium text-[#0F0F0F]">{selectedCategory}</span> category and will no longer appear in the Top Search Terms table, heatmap, or Movers &amp; Shakers section. This cannot be undone.</>
              : removalState?.kind === 'keyword-bulk'
                ? 'These keywords will be permanently removed from this category and will no longer appear anywhere in Search Trends. This cannot be undone.'
                : ''
        }
      />
    </div>
  );
}
